import {
  collection,
  doc,
  addDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import { getFile } from './library';
import type { LibraryFile, Prayer, PrayerType } from '@/types';

/**
 * Types for embedded content in community messages
 */
export type EmbeddedContentType = 'library_material' | 'prayer' | 'live_event';

export interface EmbeddedLibraryMaterial {
  type: 'library_material';
  fileId: string;
  title: string;
  description?: string;
  fileType: string;
  url: string;
  authorName?: string;
  thumbnailUrl?: string; // Custom thumbnail for video/audio
  wideThumbnailUrl?: string; // Wide aspect thumbnail for hero/featured
  // Set when the source material is deleted
  deleted?: boolean;
  deletedAt?: import('firebase/firestore').Timestamp;
}

export interface EmbeddedPrayer {
  type: 'prayer';
  prayerId: string;
  name: string;
  hebrewName?: string;
  note?: string;
  prayerType: PrayerType;
  addedByName?: string;
  // Donation support (cached from source prayer)
  donationTemplateId?: string;
  donationTemplateName?: string;
}

export interface EmbeddedLiveEvent {
  type: 'live_event';
  eventId: string;
  title: string;
  thumbnailUrl?: string;
  eventType: 'live' | 'premiere' | 'recorded';
  status: 'upcoming' | 'live' | 'ended';
  scheduledStartAt: import('firebase/firestore').Timestamp;
  scheduledEndAt?: import('firebase/firestore').Timestamp;
  hlsPlaybackUrl?: string;
  videoUrl?: string;
}

export type EmbeddedContent = EmbeddedLibraryMaterial | EmbeddedPrayer | EmbeddedLiveEvent;

/**
 * Collection paths
 */
const getMessagesPath = () => `artifacts/${appId}/public/data/messages`;
const getFilesPath = () => `artifacts/${appId}/public/data/files`;
const getPrayersPath = () => `artifacts/${appId}/public/data/prayers`;

/**
 * Post a library material to the community chat
 * Creates a message with an embedded card for the material
 */
export async function postMaterialToCommunity(
  file: LibraryFile,
  userId: string,
  userName: string,
  userRole: string,
  optionalMessage?: string
): Promise<string> {
  const messagesRef = collection(db, getMessagesPath());

  // Create the embedded content for the material
  // Only include fields that have values (Firestore doesn't accept undefined)
  const embeddedContent: Record<string, unknown> = {
    type: 'library_material',
    fileId: file.id,
    title: file.title,
    fileType: file.type,
    url: file.url,
  };

  // Only add optional fields if they have values
  if (file.description) {
    embeddedContent.description = file.description;
  }
  if (file.authorName) {
    embeddedContent.authorName = file.authorName;
  }
  if (file.thumbnailUrl) {
    embeddedContent.thumbnailUrl = file.thumbnailUrl;
  }
  if (file.wideThumbnailUrl) {
    embeddedContent.wideThumbnailUrl = file.wideThumbnailUrl;
  }

  // Build message text - either custom message or default
  const messageText =
    optionalMessage?.trim() || `📚 New material added to the library: "${file.title}"`;

  // Create the message document
  const messageData = {
    text: messageText,
    userId,
    author: userName,
    authorRole: userRole,
    timestamp: serverTimestamp(),
    // Embedded content for rendering the card
    embeddedContent,
  };

  const messageRef = await addDoc(messagesRef, messageData);

  // Update the library file to mark it as posted
  const fileRef = doc(db, getFilesPath(), file.id);
  await updateDoc(fileRef, {
    postedToCommunity: true,
    communityMessageId: messageRef.id,
    postedAt: serverTimestamp(),
    postedBy: userId,
  });

  return messageRef.id;
}

/**
 * Post a prayer to the community chat
 * Creates a message with an embedded card for the prayer
 */
export async function postPrayerToCommunity(
  prayer: Prayer,
  userId: string,
  userName: string,
  userRole: string,
  optionalMessage?: string
): Promise<string> {
  const messagesRef = collection(db, getMessagesPath());

  // Create the embedded content for the prayer
  // Only include fields that have values (Firestore doesn't accept undefined)
  const embeddedContent: Record<string, unknown> = {
    type: 'prayer',
    prayerId: prayer.id,
    name: prayer.name,
    prayerType: prayer.type,
  };

  // Only add optional fields if they have values
  if (prayer.hebrewName) {
    embeddedContent.hebrewName = prayer.hebrewName;
  }
  if (prayer.note) {
    embeddedContent.note = prayer.note;
  }
  if (prayer.addedByName) {
    embeddedContent.addedByName = prayer.addedByName;
  }
  // Include donation template info if the prayer has one linked
  if (prayer.donationTemplateId) {
    embeddedContent.donationTemplateId = prayer.donationTemplateId;
  }
  if (prayer.donationTemplateName) {
    embeddedContent.donationTemplateName = prayer.donationTemplateName;
  }

  // Build message text based on prayer type
  const prayerTypeLabels: Record<PrayerType, string> = {
    healing: 'Refuah Shleimah',
    memory: 'In Memoriam',
    dedication: 'Study Dedication',
  };

  const typeLabel = prayerTypeLabels[prayer.type] || 'Prayer';
  const hebrewSuffix = prayer.hebrewName ? ` (${prayer.hebrewName})` : '';
  const messageText =
    optionalMessage?.trim() || `🙏 New ${typeLabel} added: ${prayer.name}${hebrewSuffix}`;

  // Create the message document
  const messageData = {
    text: messageText,
    userId,
    author: userName,
    authorRole: userRole,
    timestamp: serverTimestamp(),
    // Embedded content for rendering the card
    embeddedContent,
  };

  const messageRef = await addDoc(messagesRef, messageData);

  // Update the prayer to mark it as posted
  const prayerRef = doc(db, getPrayersPath(), prayer.id);
  await updateDoc(prayerRef, {
    postedToCommunity: true,
    communityMessageId: messageRef.id,
    postedAt: serverTimestamp(),
    postedBy: userId,
  });

  return messageRef.id;
}

/**
 * Post a library material by ID (fetches the file first)
 */
export async function postMaterialToCommunityById(
  fileId: string,
  userId: string,
  userName: string,
  userRole: string,
  optionalMessage?: string
): Promise<string> {
  const file = await getFile(fileId);
  if (!file) {
    throw new Error('File not found');
  }

  return postMaterialToCommunity(file, userId, userName, userRole, optionalMessage);
}

/**
 * Check if embedded content is a library material
 */
export function isEmbeddedMaterial(content: EmbeddedContent): content is EmbeddedLibraryMaterial {
  return content.type === 'library_material';
}

/**
 * Check if embedded content is a prayer
 */
export function isEmbeddedPrayer(content: EmbeddedContent): content is EmbeddedPrayer {
  return content.type === 'prayer';
}

/**
 * Check if embedded content is a live event
 */
export function isEmbeddedLiveEvent(content: EmbeddedContent): content is EmbeddedLiveEvent {
  return content.type === 'live_event';
}

// ==================== Material Sync Functions ====================

/**
 * Update the embedded content in a community message when a material is edited
 * This keeps the community post in sync with the library material
 */
export async function syncMaterialToCommunityMessage(file: LibraryFile): Promise<void> {
  // Only sync if the file was posted to community
  if (!file.communityMessageId) {
    return;
  }

  const messageRef = doc(db, getMessagesPath(), file.communityMessageId);
  const messageSnap = await getDoc(messageRef);

  if (!messageSnap.exists()) {
    console.warn(`Community message ${file.communityMessageId} not found for file ${file.id}`);
    return;
  }

  // Build updated embedded content
  const updatedEmbeddedContent: Record<string, unknown> = {
    type: 'library_material',
    fileId: file.id,
    title: file.title,
    fileType: file.type,
    url: file.url,
  };

  if (file.description) {
    updatedEmbeddedContent.description = file.description;
  }
  if (file.authorName) {
    updatedEmbeddedContent.authorName = file.authorName;
  }
  if (file.thumbnailUrl) {
    updatedEmbeddedContent.thumbnailUrl = file.thumbnailUrl;
  }
  if (file.wideThumbnailUrl) {
    updatedEmbeddedContent.wideThumbnailUrl = file.wideThumbnailUrl;
  }

  await updateDoc(messageRef, {
    embeddedContent: updatedEmbeddedContent,
  });
}

/**
 * Delete or mark as unavailable the community message when a material is deleted
 * Option 1: Delete the entire message
 * Option 2: Mark the embedded content as deleted (preserves chat history)
 *
 * This implementation marks the content as deleted to preserve chat history
 */
export async function handleMaterialDeletedFromCommunity(
  communityMessageId: string,
  deleteMessage: boolean = false
): Promise<void> {
  const messageRef = doc(db, getMessagesPath(), communityMessageId);
  const messageSnap = await getDoc(messageRef);

  if (!messageSnap.exists()) {
    return; // Message already deleted
  }

  if (deleteMessage) {
    // Option 1: Delete the entire message
    await deleteDoc(messageRef);
  } else {
    // Option 2: Mark the embedded content as deleted (preserves chat history)
    const currentData = messageSnap.data();
    const embeddedContent = currentData.embeddedContent;

    if (embeddedContent) {
      await updateDoc(messageRef, {
        embeddedContent: {
          ...embeddedContent,
          deleted: true,
          deletedAt: serverTimestamp(),
        },
        // Update the message text to indicate the material was removed
        text: `[Material has been removed from the library]`,
      });
    }
  }
}
